import { Client, ClientOptions } from 'models/flexible/Client';
import type { Typed } from 'services/api/fields/core';

import { AttrValue, AttrValueOptions } from './AttrValue';

export const baseAttrMatchRules = {
  '*': ['%{value}'],
};

export interface AttrOptions {
  olioAttr?: string | null;
  client?: ClientOptions | null;
  attrValues?: AttrValueOptions[];
  [key: string]: any;
}
function getDefaults(): AttrOptions {
  return {
    id: '',
    clientId: '',
    createdAt: null,
    createdBy: null,
    name: '',
    displayName: '',
    olioAttr: '',
    visible: false,
    matchRules: baseAttrMatchRules,
    client: null,
    active: true,
  };
}

// Forward declare so _Attr's constructor can return the infused type
let Attr: new <T extends AttrOptions>(options: T) => Typed<_Attr<T>, T>;

class _Attr<T extends AttrOptions = AttrOptions> {
  olioAttr!: string | null;
  client!: Client<NonNullable<T['client']>> | null;
  attrValues?: AttrValue<NonNullable<T['attrValues']>[number]>[];

  constructor(options: T) {
    const opts = { ...getDefaults(), ...options };
    Object.assign(this, opts);

    this.olioAttr = opts.olioAttr ?? null;
    this.client = opts.client ? new Client<NonNullable<T['client']>>(opts.client) : null;
    this.attrValues = opts.attrValues?.map(
      (attrValue) => new AttrValue<NonNullable<T['attrValues']>[number]>(attrValue)
    );
  }

  get olioAttrObjectType(): string | null {
    if (!this.olioAttr) return null;

    return this.olioAttr.match(/classification/i) ? 'Classification' : 'Group';
  }

  get isGroupType(): boolean {
    return this.olioAttrObjectType === 'Group';
  }

  get isClassificationType(): boolean {
    return this.olioAttrObjectType === 'Classification';
  }
}

Attr = _Attr as typeof Attr; // eslint-disable-line prefer-const

export { Attr };
export type Attr<T extends AttrOptions = AttrOptions> = _Attr<T> & Omit<T, keyof _Attr<T>>;
